import { HttpClient } from '@angular/common/http';
import { Component, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatCalendar } from '@angular/material/datepicker';
import { DataTableDirective } from 'angular-datatables';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { Subject } from 'rxjs';
import { DataTablesResponse } from 'src/app/interfaces/data-table-response.interface';
import { PermissionsService } from 'src/app/services/permissions.service';
import { environment } from 'src/environments/environment';
import { helper } from 'src/environments/helper';
import { PlatiService } from './plati.service';
import { AppFilesService } from 'src/app/services/app-files.service';

@Component({
    selector: 'app-plati',
    templateUrl: './plati.component.html',
    styleUrls: ['./plati.component.scss']
})
export class PlatiComponent {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;
    @ViewChild(PopoverDirective) popover!: PopoverDirective;

    helper = helper;
    isLoading = false;
    showCard: boolean = false;
    isActive: boolean = true;
    totalIncasat: number = 0;

    defaultParameter: { name: string; id: string } = {
        name: 'Toate',
        id: '-1',
    };

    metodePlata: { name: string; id: string }[] = [
        { name: '--Alegeți--', id: '-1' },
        { name: 'Euplătesc(card)', id: '1' },
        { name: 'Selfpay', id: '2' },
        // { name: 'BT Mobile', id: '3' },
    ];

    // Popover dates
    @ViewChild('startCalendar') startCalendar!: MatCalendar<Date>;
    @ViewChild('endCalendar') endCalendar!: MatCalendar<Date>;

    today: Date = new Date();
    tomorrow: Date = new Date();
    selectedStartDate: Date = new Date();
    private _selectedEndDate: Date = new Date();
    selectedFilteringButton: string = 'last_0_days';

    setLastXDays(numberOfDays: number): void {
        const date = new Date();
        date.setDate(this.today.getDate() - numberOfDays);

        if (numberOfDays == 1) {
            this.selectedStartDate = this.selectedEndDate = date
        } else {
            this.selectedStartDate = date;
            this.selectedEndDate = new Date();
        }

        this.updateCalendarView(this.startCalendar, date);
        this.selectedFilteringButton = `last_${numberOfDays}_days`;
    }

    setYear(yearsBack: number): void {
        const year = this.today.getFullYear() - yearsBack;
        this.selectedStartDate = new Date(year, 0, 1); // 1 Ianuarie
        this.updateCalendarView(this.startCalendar, this.selectedStartDate);
        this.selectedFilteringButton =
            yearsBack === 0 ? 'current_year' : 'previous_year';
    }

    setAllYears(): void {
        this.selectedStartDate = new Date(2018, 7, 1); // 1 August
        this.updateCalendarView(this.startCalendar, this.selectedStartDate);
        this.selectedFilteringButton = 'all_years';
    }

    updateCalendarView(calendar: MatCalendar<Date>, date: Date): void {
        if (calendar) {
            calendar.activeDate = date;
        }
    }

    // Methods used to dinamically select the start date according to the end date
    get selectedEndDate(): Date {
        return this._selectedEndDate;
    }

    set selectedEndDate(date: Date) {
        this._selectedEndDate = date;
        if (this.selectedStartDate > date) {
            this.selectedStartDate = new Date(date);
        }
    }

    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();
    dtInstance!: DataTables.Api;

    closePopover(): void {
        if (this.popover) {
            this.popover.hide();
        }
    }

    searchParamsForm!: FormGroup;

    //Form Initial Values
    initialFormValues = {
        cetatean: '',
        metoda_plata: '-1',
        cnp_cui: '',
    };

    buildSearchParamsForm() {
        this.searchParamsForm = new FormGroup({
            cetatean: new FormControl(''),
            metoda_plata: new FormControl('-1'),
            cnp_cui: new FormControl(''),
        });
    }

    resetForm() {
        this.searchParamsForm.reset(this.initialFormValues);

        this.rerenderDT();
        this.showCard = true;
    }

    applyFilters(): void {
        this.showCard = false;
        this.rerenderDT(true);
    }

    dtLink =
        environment.interop.basePath +
        environment.interop.deps.plati.listDT;

    constructor(
        private http: HttpClient,
        public permissionsService: PermissionsService,
        private platiService: PlatiService,
        private appFilesService: AppFilesService,
    ) { }

    ngOnInit(): void {
        this.renderDT();
        this.buildSearchParamsForm();
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }

    formatDate(date: Date) {
        let d = new Date(date),
            month = '' + (d.getMonth() + 1),
            day = '' + d.getDate(),
            year = d.getFullYear();

        if (month.length < 2) month = '0' + month;
        if (day.length < 2) day = '0' + day;

        return [year, month, day].join('-');
    }

    renderDT() {
        this.isLoading = true;
        this.dtOptions = {
            serverSide: true,
            searching: false,
            responsive: true,
            pageLength: 25,

            ajax: (dataTablesParameters: any, callback: any) => {
                this.isLoading = true;

                dataTablesParameters.dp_payments_status = this.isActive ? 1 : 0; // Allows date filtering
                dataTablesParameters.date_from_payment = this.formatDate(this.selectedStartDate);;
                dataTablesParameters.date_to_payment = this.formatDate(this.selectedEndDate);

                dataTablesParameters.cetatean = this.searchParamsForm.value.cetatean;
                dataTablesParameters.metoda_plata = this.searchParamsForm.value.metoda_plata;
                dataTablesParameters.cnp_cui = this.searchParamsForm.value.cnp_cui;

                this.http
                    .post<DataTablesResponse>(this.dtLink, dataTablesParameters)
                    .subscribe((resp: any) => {
                        this.isLoading = false;
                        this.totalIncasat = resp.data.total_incasat;
                        callback({
                            recordsTotal: resp.data.recordsTotal,
                            recordsFiltered: resp.data.recordsFiltered,
                            data: resp.data.data,
                        });
                    });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: 'ID',
                    data: 'id_invoice',
                },
                {
                    title: 'Referință',
                    data: 'reference',
                },
                {
                    title: 'Cetățean',
                    data: 'fullname',
                    orderable: false,
                },
                // {
                //     title: 'CNP/CUI',
                //     data: 'cnp',
                //     orderable: false,
                // },
                {
                    title: 'Dată adăugat',
                    data: 'created_at',
                },
                {
                    title: 'Status',
                    data: 'status_invoice',
                    orderable: false,
                },
                {
                    title: 'Tip taxă',
                    data: 'denumire_taxa',
                    orderable: false,
                },
                {
                    title: 'Plată',
                    data: 'plata',
                    orderable: false,
                },
                {
                    title: 'Total factură',
                    data: 'total_invoice',
                    orderable: false,
                },
                {
                    title: 'Metoda',
                    data: 'metoda_plata',
                    orderable: false,
                },
                {
                    title: 'Total plătit',
                    data: 'total_payment',
                    orderable: false,
                },
                {
                    title: 'Acțiuni',
                    data: null,
                    className: 'all',
                    orderable: false,
                    render: (data: any, type: any, row: any, met: any) => {
                        return (
                            `
                            <div class="d-flex align-center justify-content-left">
                            <button type="button" class="btn btn-sm btn-primary pull-right download" title="Descarcă">
                                Descarcă
                            </button>
                            </div>
                            `
                        );
                    },
                },
            ],
            rowCallback: (row: Node, data: any[] | any) => {
                const lastTd = (row as HTMLElement).querySelector('td:last-child');
                const downloadButton = lastTd?.querySelector('.download') as HTMLElement;

                if (downloadButton) {
                    if (!this.permissionsService.hasPermission('deps-module.plati.download')) {
                        downloadButton.style.display = 'none';
                    }
                    downloadButton.addEventListener('click', () => {
                        const { id_invoice } = data;
                        this.downloadPayment(id_invoice);
                    });
                }

                return row;
            },
        };
    }

    rerenderDT(resetPaging = false): void {
        this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
            dtInstance.draw(resetPaging);
        });
    }

    toggleIsActive() {
        this.isActive = !this.isActive;
    }

    downloadPayment(id: number) {
        const fullPath = this.platiService.printChitantaURL + id;
        this.isLoading = true;
        this.appFilesService.downloadFile(fullPath)
            .then(() => {
                this.isLoading = false;
            })
            .catch(err => {
                console.error('File download error: ', err);
                this.isLoading = false;
            })
    }
}
